import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { findUser, saveFidelityForm, handleUserUpdate } from '../../../../../actions/userActions';
import useAxios from '../../../../../axios/useAxios';
import BackButton from '../../../../../components/button/BackButton';
import PrimaryButton from '../../../../../components/button/PrimaryButton';
import {
  DateInput,
  EmailInput,
  FormSection,
  GenderSelector,
  TextInput,
} from '../../../../../components/inputs';
import FormProgressBar from '../../../../../components/progress/FormProgressBar';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AddressInputGroup from '../../../../../components/inputs/AddressInputGroup';
import { PhoneInputComponent } from '../../../../../components/phoneLogin/PhoneInput';
import { ACCOUNT } from '../../../../../constants/routes';
import { notifyError, notifySuccess } from '../../../../../utils/Notify';
import { isValidName, isValidGender, AVAILABLE_GENDERS } from '../../../../../utils/Validator';
import {
  createFieldChangeHandler,
  getMaximumBirthDate,
  getMinimumBirthDate,
  transformFormDataToFlat,
} from '../../../../../utils/updateUserFonctions';
import StoreSearchStep from '../../add-card/steps/StoreSearchStep';
import { initializeMatchFidelityFormDataFromUser } from '../../add-card/utils/formInitializers';
import {
  excludeUserOwnedData,
  getUserDataWithFidelityPriority,
  calculateFormProgress,
} from '../utils/formHelpers';
import { useStores } from '../../add-card/steps/StoreSearchStep';

const MatchFidelityForm = () => {
  const user = useSelector((state) => state.user);
  const { store } = useSelector((state) => state.store);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const axiosInstance = useAxios();
  const formRef = useRef(null);

  const storeIdFromNavigation = location.state?.storeId;
  const previousRoute = location.state?.previousRoute;
  const companyInfo = location.state?.companyInfo;

  const [formData, setFormData] = useState(() =>
    initializeMatchFidelityFormDataFromUser(user),
  );
  const [isValidPhoneNumber, setIsValidPhoneNumber] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [isValidBirthDate, setIsValidBirthDate] = useState(false);
  const [isValidGenderValue, setIsValidGenderValue] = useState(true);
  const {company} = useStores(companyInfo?.id, storeIdFromNavigation);
  const minimumBirthDate = getMinimumBirthDate();
  const maximumBirthDate = getMaximumBirthDate();

  useEffect(() => {
    if (user?._id && companyInfo?.id) {
      const sourceData = getUserDataWithFidelityPriority(user, companyInfo?.id);
      const initialFormData = initializeMatchFidelityFormDataFromUser(sourceData);
      
      // Ensure phone starts with 33 if empty
      if (!initialFormData.telephone) {
        initialFormData.telephone = '33';
      }

      // Check if favorite store is valid (exists in company stores)
      const currentStoreId = storeIdFromNavigation || store?.id || '';
      const isFavoriteStoreValid = initialFormData.favoriteStore && 
        company?.stores?.some(s => s.id === initialFormData.favoriteStore);
      
      // Use favorite store only if valid, otherwise default to current store
      if (!initialFormData.favoriteStore || !isFavoriteStoreValid) {
        initialFormData.favoriteStore = currentStoreId;
      }
      
      setFormData(initialFormData);
      
      // Auto-scroll logic
      setTimeout(() => {
        let targetClass = null;
        if (!initialFormData.coordonnees?.nom || !initialFormData.coordonnees?.prenom) {
          targetClass = ".scroll-target-coordonnees";
        } else if (!initialFormData.genre) {
          targetClass = ".scroll-target-genre";
        } else if (!initialFormData.dateNaissance) {
          targetClass = ".scroll-target-date";
        } else if (!initialFormData.telephone) {
          targetClass = ".scroll-target-telephone";
        } else if (!initialFormData.favoriteStore) {
          targetClass = ".scroll-target-store";
        }

        if (targetClass) {
          const sectionRef = document.querySelector(targetClass);
          sectionRef?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 800); // Wait for form initialization and render
    } else {
      navigate(ACCOUNT);
    }
  }, [user, navigate, companyInfo]);

  // Validate gender whenever it changes
  useEffect(() => {
    if (formData.genre) {
      setIsValidGenderValue(isValidGender(formData.genre));
    } else {
      setIsValidGenderValue(true); // Empty is valid (will be caught by required check)
    }
  }, [formData.genre]);

  const handleChange = createFieldChangeHandler(setFormData);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!store.name) {
      notifyError('Veuillez sélectionner un magasin');
      return;
    }
    //verifier si l'adresse est valide
    const isAddressComplete = formData.adresse?.rue && formData.adresse?.ville && formData.adresse?.codePostal;
    
    // Create a copy of formData to manipulate
    const dataToProcess = { ...formData };
    
    // If address is not valid/complete, remove it so it's not saved
    if (!isAddressComplete) {
       delete dataToProcess.adresse;
    }

    const formDataFlat = transformFormDataToFlat(dataToProcess);
    const updatedFormData = excludeUserOwnedData(formDataFlat, user);
    
    await dispatch(saveFidelityForm(user._id, formDataFlat, store, companyInfo?.id, axiosInstance));
    
    await dispatch(handleUserUpdate(updatedFormData, axiosInstance, navigate, previousRoute));
  };

  const isFormValid =
    isValidName(formData.coordonnees?.nom) &&
    isValidName(formData.coordonnees?.prenom) &&
    isValidEmail &&
    formData.genre &&
    isValidGenderValue &&
    formData.dateNaissance &&
    isValidBirthDate &&
    isValidPhoneNumber &&
    formData.favoriteStore;

  const isFavoriteStoreValid = company?.stores?.find(store => store.id === formData.favoriteStore);

  const progress = calculateFormProgress(formData, isValidEmail, isValidBirthDate, isValidPhoneNumber, isValidGenderValue);

  return (
    <div className="update-user-container" ref={formRef}>
      
      <div className="fixed top-0 left-0 right-0 z-50 bg-white px-4 py-6 shadow-sm transition-all duration-300">
        <div className="flex items-center gap-4 mt-4">
          <button 
            onClick={() => navigate(previousRoute || ACCOUNT)}
            className="w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-md  hover:bg-gray-50 transition-colors"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="w-6 h-6" />
          </button>
          <div className="flex-1">
              <FormProgressBar percentage={progress} badge="F" />
          </div>
        </div>
      </div>

      <div className="form-header mt-32" style={{ '--delay': '0s' }}>
        <h1>Complète tes informations</h1>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Coordonnées */}
        <FormSection
          emoji="👤"
          title="Coordonnées"
          className="scroll-target-coordonnees"
          isCompleted={
            !!formData.coordonnees.nom && !!formData.coordonnees.prenom
          }
          percentage={30}
          delay="0.5s"
        >
          <TextInput
            placeholder="Nom"
            maxLength={30}
            value={formData.coordonnees.nom}
            onChange={(value) => handleChange('coordonnees', 'nom', value)}
            isValid={formData.coordonnees?.nom && isValidName(formData.coordonnees.nom)}
          />
          <TextInput
            placeholder="Prénom"
            maxLength={30}
            value={formData.coordonnees.prenom}
            onChange={(value) => handleChange('coordonnees', 'prenom', value)}
            isValid={formData.coordonnees?.prenom && isValidName(formData.coordonnees.prenom)}
          />
          <EmailInput
            value={formData.coordonnees.email}
            onChange={(value) => handleChange('coordonnees', 'email', value)}
            onValidationChange={setIsValidEmail}
            isValid={isValidEmail && formData.coordonnees?.email}
          />
        </FormSection>

        {/* Genre */}
        <FormSection
          emoji="🪢"
          title="Genre"
          className="scroll-target-genre"
          isCompleted={!!formData.genre && isValidGenderValue}
          percentage={10}
          isValid={formData.genre && isValidGenderValue}
          delay="1s"
        >
          {formData.genre && !isValidGenderValue && (
            <p className="text-[#FF2E6C] text-xl geomanistRegular font-semibold mt-2">
              Choisis ton genre.
            </p>
          )}
          <GenderSelector
            value={formData.genre}
            onChange={(value) => handleChange('genre', '', value)}
            showError={!formData.genre}
          />
        </FormSection>

        {/* Date de naissance */}
        <FormSection
          emoji="🎂"
          title="Date de naissance"
          className="scroll-target-date"
          isCompleted={!!formData.dateNaissance}
          percentage={10}
          isValid={isValidBirthDate && formData.dateNaissance}
          delay="1.5s"
        >
          {formData.dateNaissance && !isValidBirthDate && (
            <p className="text-[#FF2E6C] text-xl geomanistRegular font-semibold mt-2">
              Tu dois avoir au moins 18 ans pour continuer.
            </p>
          )}
          <DateInput
            value={formData.dateNaissance}
            onChange={(value) => handleChange('dateNaissance', '', value)}
            onValidationChange={setIsValidBirthDate}
            max={minimumBirthDate.toISOString().split('T')[0]}
            min={maximumBirthDate.toISOString().split('T')[0]}
            isValid={isValidBirthDate && formData.dateNaissance}
          />
        </FormSection>


        {/* Téléphone */}
        <FormSection
          emoji="📞"
          title="Téléphone"
          className="scroll-target-telephone"
          isCompleted={!!formData.telephone && formData.telephone.length >= 10}
          percentage={10}
          isValid={isValidPhoneNumber && formData.telephone}
          delay="2s"
        >
          <PhoneInputComponent
            value={formData.telephone}
            onChange={(value) => handleChange('telephone', '', value)}
            onValidationChange={setIsValidPhoneNumber}
            isValid={(!formData.telephone || formData.telephone === '33' || formData.telephone === '+33') ? true : isValidPhoneNumber}
            displayCheckMark={isValidPhoneNumber}
            onlyFrenchNumbers={true}
          />
        </FormSection>

        {/* Magasin préféré */}
        <FormSection
          emoji="🏠"
          title="Magasin préféré"
          className="scroll-target-store"
          isCompleted={!!formData.favoriteStore}
          percentage={10}
          delay="2.5s"
          isValid={formData.favoriteStore}
        >
          {(!formData.favoriteStore || !isFavoriteStoreValid) && (
            <p className="text-[#FF2E6C] text-xl geomanistRegular font-semibold">
              Ton magasin préféré est obligatoire pour continuer.
            </p>
          )}

          <StoreSearchStep
            value={formData.favoriteStore}
            onChange={(value) => handleChange('favoriteStore', '', value)}
            error={!formData.favoriteStore || !isFavoriteStoreValid ? 'Sélectionne un magasin' : ''}
            storeId={storeIdFromNavigation || store?.id}
            viewNextButton={false}
          />
        </FormSection>

        {/* Adresse (optionnel) */}
        <FormSection
          emoji="📍"
          title="Adresse"
          isCompleted={!!formData.adresse?.rue && !!formData.adresse?.ville && !!formData.adresse?.codePostal}
          isOptional={true}
          delay="3s"
        >
          <AddressInputGroup
            value={formData.adresse}
            onChange={(field, value) => handleChange('adresse', field, value)}
          />
        </FormSection>
      </form>

      <div className="submit-button-container">
        <PrimaryButton
          disabled={!isFormValid}
          label={'Enregistrer'}
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
};

export default MatchFidelityForm;
